import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getExtensionDownloadUrl } from '../services/bookmark/extensionConfigService';

const env = (import.meta as { env?: Record<string, string> }).env;
const FALLBACK_URL =
  env?.VITE_EXTENSION_ZIP_URL ||
  (env?.VITE_SUPABASE_URL
    ? `${env.VITE_SUPABASE_URL}/storage/v1/object/public/extension-releases/extBookMark-extension-latest.zip`
    : '');

const BookmarkInstallPage: React.FC = () => {
  const navigate = useNavigate();
  const [downloadUrl, setDownloadUrl] = useState<string>(FALLBACK_URL);
  const [loading, setLoading] = useState(!!env?.VITE_SUPABASE_URL && !env?.VITE_EXTENSION_ZIP_URL);

  useEffect(() => {
    if (env?.VITE_EXTENSION_ZIP_URL) return;
    getExtensionDownloadUrl().then((url) => {
      if (url) setDownloadUrl(url);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className="max-w-2xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/bookmarks')}
          className="mb-6 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          返回书签
        </button>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">安装 extBookMark 扩展</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          扩展用于备份浏览器书签到云端、从云端还原到书签栏。支持 Chrome、Edge。
        </p>

        {loading ? (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">1. 下载扩展</h2>
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
              <span>加载下载链接...</span>
            </div>
          </section>
        ) : downloadUrl ? (
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">1. 下载扩展</h2>
            <a
              href={downloadUrl}
              download
              className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground hover:opacity-90 font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              下载 extBookMark 扩展 (zip)
            </a>
          </section>
        ) : (
          <section className="mb-8 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200">
            <p className="text-sm">
              扩展 zip 尚未配置下载链接。请本地构建后手动加载：
            </p>
            <code className="block mt-2 p-2 rounded bg-gray-100 dark:bg-gray-800 text-sm">
              pnpm build:extension
            </code>
            <p className="mt-2 text-sm">
              构建产物在 <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">packages/extension/dist</code>，
              在 Chrome 扩展管理页选择「加载已解压的扩展程序」并选择该目录。
            </p>
          </section>
        )}

        <section className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
            {downloadUrl ? '2' : '1'}. 安装步骤
          </h2>
          <ol className="list-decimal list-inside space-y-4 text-gray-700 dark:text-gray-300">
            <li>
              打开扩展管理页：
              <ul className="mt-1 ml-6 list-disc">
                <li>Chrome: 地址栏输入 <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">chrome://extensions</code></li>
                <li>Edge: 地址栏输入 <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">edge://extensions</code></li>
              </ul>
            </li>
            <li>开启右上角「开发者模式」</li>
            <li>
              {downloadUrl ? (
                <>解压下载的 zip，点击「加载已解压的扩展程序」，选择解压后的文件夹</>
              ) : (
                <>点击「加载已解压的扩展程序」，选择 <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">packages/extension/dist</code> 目录</>
              )}
            </li>
            <li>安装完成后，刷新本页面即可使用「备份」「还原」功能</li>
          </ol>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">使用说明</h2>
          <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
            <li>• <strong>备份</strong>：将浏览器书签栏中的书签上传到云端（会覆盖当前云端数据）</li>
            <li>• <strong>还原</strong>：将云端书签写入浏览器书签栏（会新建文件夹，不覆盖现有书签）</li>
            <li>• 需在书签页（与扩展同源）操作，请确保已登录</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default BookmarkInstallPage;
